import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MainFooter from '../../components/layout/MainFooter';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Icon from '../../components/AppIcon';
import '../../styles/community-guidelines.css';

const CommunityGuidelinesPage = () => {
  const [expandedSection, setExpandedSection] = useState('core-values');

  const guidelines = {
    'core-values': {
      title: 'Core Values',
      icon: 'Heart',
      subsections: [
        {
          heading: 'Respect & Professionalism',
          content: 'We believe in fostering a respectful environment where all members treat each other with dignity. Whether you\'re a Virtual Assistant or an Employer, professionalism is the foundation of successful working relationships on VA Sprouts.'
        },
        {
          heading: 'Integrity & Honesty',
          content: 'Be truthful in your profiles, job descriptions, and communications. Accurate information helps create meaningful connections and sets realistic expectations for both parties.'
        },
        {
          heading: 'Trust & Safety',
          content: 'We prioritize the safety and security of all community members. Never share sensitive personal or financial information outside of our secure platform.'
        },
        {
          heading: 'Collaboration & Support',
          content: 'Our community thrives on mutual support. Share knowledge, help others succeed, and contribute to building a positive marketplace for virtual work.'
        }
      ]
    },
    'va-guidelines': {
      title: 'Virtual Assistant Guidelines',
      icon: 'User',
      subsections: [
        {
          heading: 'Profile Accuracy',
          content: 'Your profile is your professional brand. Keep it updated with accurate information about your skills, experience, hourly rate, and availability. Use a professional photo and write a compelling bio that highlights your strengths.'
        },
        {
          heading: 'Professional Communication',
          content: 'Respond to messages promptly and professionally. Communicate clearly about your availability, capabilities, and any constraints. Avoid unsolicited contact or spam.'
        },
        {
          heading: 'Quality Work Delivery',
          content: 'Deliver work that meets or exceeds agreed-upon standards. Meet deadlines consistently and communicate proactively if issues arise. Your reputation depends on reliable, quality work.'
        },
        {
          heading: 'Skills & Certifications',
          content: 'Be honest about your skills and certifications. Only list skills you\'ve actually developed. Take advantage of our Skills Assessment program to verify your abilities and increase your credibility with employers.'
        },
        {
          heading: 'Confidentiality & NDA Compliance',
          content: 'Respect any non-disclosure agreements or confidentiality requirements set by your employers. Never share client information, work product, or business strategies without explicit permission.'
        },
        {
          heading: 'Availability & Reliability',
          content: 'Be upfront about your working hours and availability. If you commit to a project, follow through on your obligations. Communicate any changes to your availability in advance.'
        }
      ]
    },
    'employer-guidelines': {
      title: 'Employer Guidelines',
      icon: 'Briefcase',
      subsections: [
        {
          heading: 'Clear Job Descriptions',
          content: 'Provide detailed, honest job descriptions that clearly outline responsibilities, required skills, timeline, and compensation. Ambiguous postings lead to mismatched hires and wasted time for everyone.'
        },
        {
          heading: 'Fair Compensation',
          content: 'Offer compensation that reflects the skill level and market rates for the work required. Undercutting rates or attempting to exploit cheap labor undermines our community and is not tolerated.'
        },
        {
          heading: 'Respectful Treatment',
          content: 'Treat Virtual Assistants with the same respect you\'d show to in-house employees. Provide constructive feedback, acknowledge good work, and maintain professional communication.'
        },
        {
          heading: 'Timely Payment',
          content: 'Pay invoices on time as agreed. Honor your financial commitments. Late or disputed payments damage trust and are grounds for disciplinary action.'
        },
        {
          heading: 'Realistic Expectations',
          content: 'Set clear expectations about deliverables, communication frequency, and project scope. Don\'t change requirements mid-project without discussing impact on timeline and cost.'
        },
        {
          heading: 'Professional Conduct',
          content: 'Maintain appropriate professional boundaries. Avoid making personal demands or requesting work outside the agreed scope. Discriminatory or harassing behavior will result in account suspension.'
        }
      ]
    },
    'prohibited-conduct': {
      title: 'Prohibited Conduct',
      icon: 'AlertCircle',
      subsections: [
        {
          heading: 'Discrimination & Harassment',
          content: 'Any form of discrimination based on race, gender, age, religion, ethnicity, sexual orientation, disability, or other protected characteristics is strictly prohibited. Harassment, bullying, or intimidating behavior will not be tolerated.'
        },
        {
          heading: 'Fraud & Misrepresentation',
          content: 'Don\'t falsify credentials, experience, or qualifications. Don\'t use fake profiles or attempt to deceive other members about your identity, skills, or services. Fraudulent activity will result in immediate account termination.'
        },
        {
          heading: 'Scams & Financial Exploitation',
          content: 'Don\'t attempt to scam, defraud, or financially exploit other members. This includes pyramid schemes, money laundering, or any illegal financial schemes.'
        },
        {
          heading: 'Intellectual Property Violations',
          content: 'Respect intellectual property rights. Don\'t upload copyrighted content, plagiarized work, or materials you don\'t have permission to use. VAs must deliver original work unless expressly authorized otherwise.'
        },
        {
          heading: 'Solicitation Outside the Platform',
          content: 'Don\'t attempt to move transactions outside VA Sprouts to avoid platform fees or security measures. All job-related communication should remain on our platform until a contract is established.'
        },
        {
          heading: 'Inappropriate Content',
          content: 'Don\'t share sexually explicit, violent, hateful, or otherwise inappropriate content. Keep all communication professional and workplace-appropriate.'
        },
        {
          heading: 'Spam & Unsolicited Contact',
          content: 'Don\'t send unsolicited messages, bulk communications, or spam. Respect the "Do Not Contact" preferences of other members. Persistent spam will result in account restrictions.'
        },
        {
          heading: 'Data Breaches & Security Violations',
          content: 'Don\'t attempt to breach platform security, access unauthorized information, or engage in hacking. Report any security vulnerabilities to our support team immediately.'
        }
      ]
    },
    'dispute-resolution': {
      title: 'Dispute Resolution & Appeals',
      icon: 'Scale',
      subsections: [
        {
          heading: 'How We Handle Disputes',
          content: 'If a dispute arises between an employer and VA, we encourage open communication first. If unresolved, submit a formal dispute through our support system with documentation of the issue.'
        },
        {
          heading: 'Our Review Process',
          content: 'Our support team will review all evidence from both parties fairly and impartially. We aim to resolve disputes within 7-10 business days. Decisions are based on contract terms, platform policies, and evidence provided.'
        },
        {
          heading: 'Appealing Decisions',
          content: 'If you disagree with a decision, you may submit an appeal within 14 days with new evidence or information. A different team member will review your appeal to ensure fairness.'
        },
        {
          heading: 'Mediation Services',
          content: 'For significant disputes, we offer optional mediation with a neutral third party. Both parties must agree to mediation for it to proceed.'
        }
      ]
    },
    'enforcement': {
      title: 'Enforcement & Consequences',
      icon: 'Shield',
      subsections: [
        {
          heading: 'Warnings',
          content: 'Minor violations may result in a warning and opportunity to correct the behavior. We\'ll notify you of the violation and expected remediation.'
        },
        {
          heading: 'Suspension',
          content: 'Repeated violations or serious misconduct may result in temporary account suspension (7 days to 30 days). Your account will be locked during this period.'
        },
        {
          heading: 'Termination',
          content: 'Severe violations including fraud, harassment, discrimination, or illegal activity will result in permanent account termination. Terminated accounts cannot be reinstated.'
        },
        {
          heading: 'Transparency',
          content: 'We\'ll inform you of any action taken against your account and the reason for it. You have the right to appeal enforcement decisions.'
        }
      ]
    },
    'community-features': {
      title: 'Community Features & Expectations',
      icon: 'Users',
      subsections: [
        {
          heading: 'Reviews & Ratings',
          content: 'Leave honest, constructive reviews based on your actual experience. Avoid retaliatory or false reviews. Fake reviews are grounds for account suspension.'
        },
        {
          heading: 'Skills & Assessments',
          content: 'Participate in skills assessments to build credibility. Cheating or attempting to artificially inflate your assessment scores will result in removal from the program.'
        },
        {
          heading: 'Help & Support',
          content: 'Use our help center and support resources. Ask questions, share feedback, and help other community members. Be respectful in all community interactions.'
        },
        {
          heading: 'Feedback & Reporting',
          content: 'We welcome constructive feedback about our platform. If you encounter violations, report them immediately to support. Do not engage in public shaming or call-out posts.'
        }
      ]
    },
    'privacy-security': {
      title: 'Privacy & Security',
      icon: 'Lock',
      subsections: [
        {
          heading: 'Personal Information Protection',
          content: 'Never share your password with anyone. Keep your login credentials secure. Report any unauthorized account access immediately.'
        },
        {
          heading: 'Data Privacy',
          content: 'Your data is important to us. We protect your personal information according to our Privacy Policy. We will never sell your data to third parties without consent.'
        },
        {
          heading: 'Payment Security',
          content: 'All payment information is encrypted and secured. Never enter payment details outside our secure payment system. We use industry-standard security protocols.'
        },
        {
          heading: 'Two-Factor Authentication',
          content: 'Enable two-factor authentication on your account for additional security. This helps protect your account from unauthorized access.'
        }
      ]
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Community Guidelines | VA Sprouts</title>
        <meta name="description" content="Learn about VA Sprouts community guidelines, expectations, and code of conduct for virtual assistants and employers." />
        <meta name="keywords" content="community guidelines, code of conduct, VA Sprouts policies" />
      </Helmet>

      <VAHeaderNav />

      {/* Hero Section */}
      <div className="bg-primary-600 text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Community Guidelines</h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">
              VA Sprouts is built on trust, professionalism, and mutual respect. These guidelines help us maintain a positive, safe community for all members.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Table of Contents */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Navigation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(guidelines).map(([key, section]) => (
              <button
                key={key}
                onClick={() => toggleSection(key)}
                className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all text-left"
              >
                <Icon name={section.icon} size={24} className="text-primary flex-shrink-0" />
                <span className="font-medium text-gray-900 hover:text-primary transition-colors">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Guidelines Sections */}
        <div className="space-y-6">
          {Object.entries(guidelines).map(([key, section]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon name={section.icon} size={24} className="text-primary" />
                  <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                </div>
                <Icon
                  name={expandedSection === key ? 'ChevronUp' : 'ChevronDown'}
                  size={24}
                  className="text-gray-500 transition-transform"
                />
              </button>

              {expandedSection === key && (
                <div className="border-t border-gray-200">
                  <div className="px-6 py-6 space-y-6">
                    {section.subsections.map((subsection, idx) => (
                      <div key={idx}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{subsection.heading}</h3>
                        <p className="text-gray-700 leading-relaxed">{subsection.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Key Principles Section */}
        <div className="mt-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg border border-primary-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Honest</h3>
                <p className="text-gray-700">Truthfulness in profiles, descriptions, and communications builds trust.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Professional</h3>
                <p className="text-gray-700">Maintain professional standards in all interactions and work delivery.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Respectful</h3>
                <p className="text-gray-700">Treat all community members with dignity and courtesy.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Responsible</h3>
                <p className="text-gray-700">Take accountability for your actions and commitments.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Safe</h3>
                <p className="text-gray-700">Protect your data and report suspicious activity immediately.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Icon name="CheckCircle" size={24} className="text-primary mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Fair</h3>
                <p className="text-gray-700">Ensure fair treatment, compensation, and working conditions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Report Violations Section */}
        <div className="mt-12 bg-blue-50 rounded-lg border border-blue-200 p-8">
          <div className="flex gap-4">
            <Icon name="AlertCircle" size={32} className="text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Found a Violation?</h2>
              <p className="text-gray-700 mb-4">
                If you encounter any behavior that violates these guidelines, please report it immediately to our support team. Include specific details, timestamps, and any relevant screenshots or evidence.
              </p>
              <Link
                to="/help-support"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <Icon name="MessageSquare" size={20} />
                Contact Support
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <details className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors">
              <summary className="font-semibold text-gray-900 flex justify-between items-center">
                What happens if I accidentally violate a guideline?
                <Icon name="ChevronDown" size={20} className="text-gray-500" />
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Minor violations typically result in a warning. We believe in education and improvement. You'll have the opportunity to correct the behavior. Only repeated or severe violations lead to suspension or termination.
              </p>
            </details>

            <details className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors">
              <summary className="font-semibold text-gray-900 flex justify-between items-center">
                Can I appeal an account suspension or termination?
                <Icon name="ChevronDown" size={20} className="text-gray-500" />
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Yes, you have the right to appeal. Submit your appeal within 14 days with new evidence or context. A different team member will review your case for fairness.
              </p>
            </details>

            <details className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors">
              <summary className="font-semibold text-gray-900 flex justify-between items-center">
                Are private conversations monitored for guideline violations?
                <Icon name="ChevronDown" size={20} className="text-gray-500" />
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                We only review conversations when reported by users or when investigating a formal complaint. Your privacy is important to us, and we only access messages when necessary.
              </p>
            </details>

            <details className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors">
              <summary className="font-semibold text-gray-900 flex justify-between items-center">
                What's the difference between these guidelines and the Terms of Service?
                <Icon name="ChevronDown" size={20} className="text-gray-500" />
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                These guidelines focus on community conduct and expectations. Our Terms of Service contain legal agreements and platform rules. Both are important—read both carefully.
              </p>
            </details>

            <details className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-primary transition-colors">
              <summary className="font-semibold text-gray-900 flex justify-between items-center">
                How are disputes between VAs and employers resolved?
                <Icon name="ChevronDown" size={20} className="text-gray-500" />
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed">
                We encourage direct communication first. If unresolved, submit a formal dispute with documentation. Our support team reviews evidence from both sides fairly and typically resolves disputes within 7-10 business days.
              </p>
            </details>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-8 border border-primary-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Questions About These Guidelines?</h2>
          <p className="text-gray-700 mb-6">
            Our support team is here to help clarify any questions about community guidelines and platform policies.
          </p>
          <Link
            to="/help-support"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            <Icon name="HelpCircle" size={20} />
            View Help Center
          </Link>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Last updated: January 2026</p>
          <p>These guidelines are subject to change. We recommend reviewing them periodically.</p>
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default CommunityGuidelinesPage;
